import React from "react";
import { Controller } from "react-hook-form";
import {
  TextField,
  Switch,
  FormControl,
  Autocomplete,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { AppendType } from "../../common/constants";

interface AppendOperationsSectionProps {
  control: any;
  watch: any;
  setValue: any;
  register: any;
  fileSpecTemplateList: any[];
  importTemplateList: any[];
  selectedImportTemplate: any;
  setSelectedImportTemplate: (template: any) => void;
  splittingCriteriaContactLists: string[];
  setSplittingCriteriaContactLists: (lists: string[]) => void;
  contactListItems: any[];
  contactListColumns: string[];
  validateSplittingCriteriaColumns: () => boolean;
  handlePopulateSplittingCriteria: () => void;
}

const AppendOperationsSection: React.FC<AppendOperationsSectionProps> = ({
  control,
  watch,
  setValue,
  register,
  fileSpecTemplateList,
  importTemplateList,
  selectedImportTemplate,
  setSelectedImportTemplate,
  splittingCriteriaContactLists,
  setSplittingCriteriaContactLists,
  contactListItems,
  contactListColumns,
  validateSplittingCriteriaColumns,
  handlePopulateSplittingCriteria,
}) => {
  return (
    <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 mt-1">
      <div className="row">
        <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-3">
          <span className="text">Use Specification File</span>
          <Controller
            name="rules.importFile.contactListConfiguration.useSpecificationFile"
            control={control}
            render={({ field }) => (
              <Switch
                {...field}
                size="small"
                checked={field.value}
              />
            )}
          />
        </div>
        <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-3">
          <div className="mb-2 d-flex">
            <FormControl sx={{ width: "100%" }} size="small">
              <Controller
                name="rules.importFile.fileSpecificationTemplateId"
                disabled={!watch("rules.importFile.contactListConfiguration.useSpecificationFile")}
                control={control}
                rules={{ required: "fileSpecificationTemplateId is required" }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    onChange={(_event, newValue) => {
                      setValue("rules.importFile.fileSpecificationTemplateId", newValue?.id || "");
                    }}
                    value={fileSpecTemplateList.find(
                      (fileTemplateList) => fileTemplateList.id === watch(field.name)
                    ) || null}
                    disablePortal
                    size="small"
                    sx={{ width: "100%" }}
                    options={fileSpecTemplateList}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="File Specification Template *"
                      />
                    )}
                  />
                )}
              />
            </FormControl>
          </div>
        </div>
        <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-3">
          <span className="text">Use Import Template</span>
          <Controller
            name="rules.importFile.contactListConfiguration.useImportTemplate"
            control={control}
            render={({ field }) => (
              <Switch
                {...field}
                size="small"
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  if (!e.target.checked) {
                    setSelectedImportTemplate(null);
                    setSplittingCriteriaContactLists([]);
                    setValue("rules.importFile.importTemplateId", "");
                  }
                }}
              />
            )}
          />
        </div>
        <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-3">
          <div className="mb-2 d-flex">
            <FormControl sx={{ width: "100%" }} size="small">
              <Controller
                name="rules.importFile.importTemplateId"
                disabled={!watch("rules.importFile.contactListConfiguration.useImportTemplate")}
                control={control}
                rules={{ required: "importTemplateId is required" }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    onChange={(_event, newValue) => {
                      setValue("rules.importFile.importTemplateId", newValue?.id || "");
                      setSelectedImportTemplate(newValue);
                      if (newValue?.useSplittingCriteria) {
                        setSplittingCriteriaContactLists([""]);
                      } else {
                        setSplittingCriteriaContactLists([]);
                      }
                    }}
                    value={importTemplateList.find(
                      (importTemplate) => importTemplate.id === watch(field.name)
                    ) || null}
                    disablePortal
                    size="small"
                    sx={{ width: "100%" }}
                    options={importTemplateList}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Import Template *"
                      />
                    )}
                  />
                )}
              />
            </FormControl>
          </div>
          {watch("rules.importFile.contactListConfiguration.useImportTemplate") && 
           (watch("rules.importFile.appendType") === AppendType.appendonly || 
            watch("rules.importFile.appendType") === AppendType.flushandappend) && (
            <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-3">
              <div className="d-flex align-items-center">
                <Tooltip title="Enter the List Name Prefix of the original lists created with this import template">
                  <TextField
                    label="Original Contact List Name *"
                    {...register("rules.importFile.originalContactListName")}
                    value={watch("rules.importFile.originalContactListName") || ""}
                    variant="outlined"
                    size="small"
                    sx={{ width: "70%", marginRight: "10px" }}
                  />
                </Tooltip>
                {selectedImportTemplate?.useSplittingCriteria && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handlePopulateSplittingCriteria}
                    disabled={!watch("rules.importFile.originalContactListName")}
                  >
                    Populate
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6">
          <div className="text-500 mt-1">Unique Identifier Column</div>
          <div className="col-8 col-sm-8 col-md-8 col-lg-8 col-xl-8 mt-1">
            <FormControl sx={{ width: "100%" }} size="small">
              <Controller
                name="rules.importFile.contactIdName"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    disablePortal
                    size="small"
                    sx={{ width: "100%" }}
                    {...field}
                    value={watch(field.name) || ""}
                    onChange={(_event, newValue) => {
                      setValue("rules.importFile.contactIdName", newValue as string);
                    }}
                    options={contactListColumns}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Column"
                        size="small"
                      />
                    )}
                  />
                )}
              />
            </FormControl>
          </div>
        </div>
        <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6">
          {selectedImportTemplate?.useSplittingCriteria && (
            <>
              <div className="text-500 mt-1">Contact Lists (max 10 per job)</div>
              {validateSplittingCriteriaColumns() && (
                <div className="text-danger mt-1 mb-2">
                  Error: All selected contact lists must have matching column structures.
                </div>
              )}
              {splittingCriteriaContactLists.map((contactListId, index) => (
                <div className="d-flex align-items-center mt-1" key={index}>
                  <FormControl sx={{ width: "100%", marginRight: "5px" }} size="small">
                    <Autocomplete
                      disablePortal
                      size="small"
                      sx={{ width: "100%" }}
                      options={contactListItems}
                      getOptionLabel={(option) => option.name}
                      value={contactListItems.find(contact => contact.id === contactListId) || null}
                      onChange={(_event, newValue) => {
                        const updatedLists = [...splittingCriteriaContactLists];
                        updatedLists[index] = newValue?.id || "";
                        setSplittingCriteriaContactLists(updatedLists);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Contact List ${index + 1}`}
                          size="small"
                        />
                      )}
                    />
                  </FormControl>
                  {splittingCriteriaContactLists.length === index + 1 && (
                    <IconButton
                      disabled={splittingCriteriaContactLists.length >= 10}
                      onClick={() => setSplittingCriteriaContactLists([...splittingCriteriaContactLists, ""])}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                  {splittingCriteriaContactLists.length !== index + 1 && (
                    <IconButton
                      onClick={() => {
                        const updatedLists = splittingCriteriaContactLists.filter((_, i) => i !== index);
                        setSplittingCriteriaContactLists(updatedLists);
                      }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppendOperationsSection;